/**
 * Multistore product recommendations popup management.
 * Handles displaying product recommendation popups after items are added to cart,
 * manages popup visibility, and updates hidden form inputs.
 * 
 * @class
 * @description This class provides:
 * - Popup display after cart additions with recommendations
 * - Hidden input management for passing recommendation data
 * - Swatch click handling to switch between product variants
 * - Slick carousel initialization for recommendation lists
 * 
 * @requires jQuery (for slick carousel)
 * 
 * @example
 * // Automatically initializes on page load
 * let multistore_recommendations = new multistoreRecommendations();
 * multistore_recommendations.init();
 */
class multistoreRecommendations {
  /**
   * Creates a new multistoreRecommendations instance.
   * @constructor
   */
  constructor() {
    /** @type {number|null} Current product ID */
    this.productId = null;
    /** @type {HTMLElement|null} Reference to popup overlay element */
    this.popupOverlay = null;
  }

  /**
   * Initializes the recommendations module.
   * Binds events and updates hidden inputs.
   * 
   * @returns {void}
   * @sideEffect Binds cart change and swatch click events, calls updateHiddenInputs
   */
  init() {
    let _this = this;
    this.bindEvents();
    this.updateHiddenInputs();

    // Listen for cart changes to show recommendations
    document.addEventListener('theme:cartchanged', (e) => {
      _this.handleCartChanged(e);
    });

    // Listen for swatch clicks to update popup context
    document.addEventListener('swatch-clicked', (e) => {
      // Delay ensures all events dispatched and elements updated
      setTimeout(() => {
        _this.handleSwatchClicked();
      }, 25);
    });
  }

  /**
   * Binds event listeners for popup interactions.
   * Sets up close button handlers and retrieves product ID.
   * 
   * @returns {void}
   * @sideEffect Queries DOM, sets productId and popupOverlay properties
   */
  bindEvents() {
    let _this = this;

    // Get product ID from input if available
    this.productId = document?.querySelector('input[name="product-id"]')?.value ?? null;
    
    // Get popup overlay reference
    if(document.querySelector('.recommendations--popup-overlay')) {
      if(!this.popupOverlay) this.popupOverlay = document.querySelector('.recommendations--popup-overlay');

      // Bind close and keep shopping buttons
      this.popupOverlay.querySelectorAll('.close, .keep-shopping').forEach(function (el) {
        el.addEventListener("click", function (e) {
          e.preventDefault();
          _this.closePopup();
        });
      });
    }
  }

  /**
   * Handles cart change events to show recommendations popup.
   * Checks if added item has recommendation data and opens popup accordingly.
   * 
   * @param {CustomEvent} event - The cart changed event detail
   * @returns {void}
   * @sideEffect May call openPopup() and buildRecommendations()
   */
  handleCartChanged(event) {
    let addedItem = event.detail;
    if(addedItem) {
      // Check if we should show recommendations (based on property)
      let useNativeRecommendations = false;
      let openPopup = false;
      
      // Check for multistore recommendations property
      if(addedItem.hasOwnProperty('properties') && addedItem.properties.hasOwnProperty('_multistore_recommended_products')) {
        let recommendationsText = addedItem.properties._multistore_recommended_products;
        // Open popup if there's recommendation text
        openPopup = recommendationsText.length > 0;
      }

      if(openPopup) {
        this.openPopup();
        this.buildRecommendations(addedItem, useNativeRecommendations);
      }
    }
  }

  /**
   * Handles swatch click to update popup for the selected variant.
   * Updates product ID and shows correct popup for current variant.
   * 
   * @returns {void}
   * @sideEffect Updates productId, popupOverlay, toggles visibility classes
   */
  handleSwatchClicked() {
    // Update product ID from current variant
    this.productId = document.querySelector('input[name="product-id"]').value;
    this.popupOverlay = document.querySelector(`.recommendations--popup-overlay[data-id="${this.productId}"]`);
    this.updateHiddenInputs();

    // Hide all other popups, show current
    document.querySelectorAll(`.recommendations--popup-overlay:not([data-id="${this.productId}"])`).forEach(function (el) {
      el.classList.remove('active');
      el.classList.add('hidden');
    });
    this.popupOverlay.classList.remove('hidden');
    this.bindEvents();
  }

  /**
   * Updates hidden input name for recommendations based on current product.
   * Ensures the correct input is submitted with the product.
   * 
   * @returns {void}
   * @sideEffect Modifies name attribute on hidden input
   */
  updateHiddenInputs() {
    if(!this.productId) return;
    
    // Find target input for current product
    let inputTarget = document.querySelector(`.multistore-recommendations-hidden-input[data-id="${this.productId}"]`);

    // Clear any existing global input
    if(document.querySelector('input[name="properties[_multistore_recommended_products]"]')) {
      document.querySelector('input[name="properties[_multistore_recommended_products]"]').setAttribute('name','');
    }

    if (!inputTarget) return;

    // Enable the correct input for this product
    let defaultTargetName = inputTarget.dataset.name;
    inputTarget.setAttribute('name', defaultTargetName);
  }

  /**
   * Builds and displays recommendations in the popup.
   * Populates product info and initializes slick carousel.
   * 
   * @param {Object} addedItem - The item that was just added to cart
   * @param {boolean} useNativeRecommendations - Whether to show native Shopify recommendations
   * @returns {void}
   * @sideEffect Updates DOM elements with product info, initializes slick carousel
   */
  buildRecommendations(addedItem, useNativeRecommendations) {
    let _this = this;
    let addedCont = this.popupOverlay.querySelector('.added-product');
    if(!addedCont) return;

    // Toggle between multistore and native recommendations
    let multistoreRecommendations = _this.popupOverlay.querySelector('.recommendations-list--container > .recommendations-list');
    let nativeRecommendations = _this.popupOverlay.querySelector('.shopify-recommendations-list');

    multistoreRecommendations.classList.toggle('hidden', useNativeRecommendations);
    nativeRecommendations.classList.toggle('hidden', !useNativeRecommendations);

    // Initialize slick carousel for recommendations
    $(_this.popupOverlay).find('.recommendations-list:not(.hidden), .shopify-recommendations-list:not(.slick-initialized):not(.hidden) .recommendations-list').slick({
      infinite: false,
      slidesToShow: 3,
      arrows: false,
      responsive: [{
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        }
      }]
    });

    // Populate added product information
    if(addedItem) {
      let imgTarget = addedCont.querySelector('.added-product--image img');
      let titleTarget = addedCont.querySelector('.added-product--title');
      let optionsTarget = addedCont.querySelector('.added-product--options');
      let qtyTarget = addedCont.querySelector('.added-product--quantity');
      let priceTarget = addedCont.querySelector('.added-product--price');

      // Update image
      if(imgTarget && addedItem.hasOwnProperty('featured_image') && addedItem.featured_image.hasOwnProperty('url')) {
        imgTarget.setAttribute('src', addedItem.featured_image.url);
      }
      // Update title
      if(titleTarget && addedItem.hasOwnProperty('untranslated_product_title')) {
        titleTarget.innerText = addedItem.untranslated_product_title;
      }
      // Update options/variant
      if(optionsTarget && addedItem.hasOwnProperty('variant_title')) {
        optionsTarget.innerText = addedItem.variant_title;
      }
      // Update quantity
      if(qtyTarget && addedItem.hasOwnProperty('quantity')) {
        qtyTarget.innerText = 'x' + addedItem.quantity;
      }
      // Update price
      if(priceTarget && addedItem.hasOwnProperty('price')) {
        priceTarget.innerText = '$' + (addedItem.price / 100).toFixed(2);
      }
    }

    this.bindEvents();
    this.openPopup();
  }

  /**
   * Opens the recommendations popup overlay.
   * 
   * @returns {void}
   * @sideEffect Adds CSS classes to show popup
   */
  openPopup() {
    this.popupOverlay.classList.add('active');
    document.querySelector('body').classList.add('noscroll');
    this.popupOpen = true;
  }

  /**
   * Closes the recommendations popup overlay.
   * 
   * @returns {void}
   * @sideEffect Removes CSS classes, destroys slick carousel
   */
  closePopup() {
    this.popupOverlay.classList.remove('active');
    document.querySelector('body').classList.remove('noscroll');
    this.popupOpen = false;
    // Destroy slick carousel when closing
    $('.recommendations-list.slick-initialized').slick('unslick');
  }
}

// Create and initialize instance
let multistore_recommendations = new multistoreRecommendations();
multistore_recommendations.init();
